import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Page, PackageDetail, PackageStatus, PackageSummary } from '@azko/shared';
import { ApiRequestError, api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { formatReceived, statusLabel, statusTone } from '../lib/format.js';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  LoadingRows,
  PageHeader,
  Pagination,
  SearchInput,
  StatusBadge,
  TextInput,
  resetting,
  useCursorPages,
} from '../components/ui.js';
import { PackageDetails } from '../components/PackageDetails.js';
import { useDebouncedValue } from '../lib/useDebouncedValue.js';

/**
 * Filtros da lista.
 *
 * "Canceladas" e uma opcao explicita porque a lista padrao NAO as mostra: para
 * quem acabou de desfazer um registro, "removi" tem de parecer removido. Mas
 * some da vista, nao do banco — e este filtro e o caminho de volta para quem
 * precisa auditar o que a portaria apagou.
 */
const FILTERS: Array<{ value: PackageStatus | ''; label: string }> = [
  { value: '', label: 'Todas' },
  { value: 'awaiting', label: 'Aguardando' },
  { value: 'picked_up', label: 'Retiradas' },
  { value: 'canceled', label: 'Canceladas' },
];

/** Motivos prontos: no balcao, cancelar tem de ser um toque, nao digitacao. */
const QUICK_REASONS = ['Registro duplicado', 'Unidade errada', 'Nao era encomenda'];

/**
 * O selo de lote — a unica coisa que distingue uma linha de tres caixas de uma
 * linha de uma caixa.
 *
 * A lista mostra REGISTROS, entao um lote de tres ocupa uma linha so, que e o
 * ganho que o porteiro veio buscar. O preco disso e que "Aguardando" numa linha
 * de lote fica ambiguo: falta uma caixa ou faltam as tres? O selo responde,
 * e por isso ele muda de texto na retirada parcial em vez de repetir o total.
 */
function BoxCountBadge({ item }: { item: PackageSummary }) {
  if (item.boxCount <= 1) return null;

  const parcial = item.boxesAwaiting > 0 && item.boxesAwaiting < item.boxCount;

  return (
    <span
      className={
        parcial
          ? 'rounded-pill border border-warn/40 bg-warn/10 px-2 py-0.5 text-[0.7rem] font-bold text-warn'
          : 'rounded-pill border border-line bg-surface-2 px-2 py-0.5 text-[0.7rem] font-bold text-muted'
      }
    >
      {parcial ? `${item.boxesAwaiting} de ${item.boxCount} caixas` : `${item.boxCount} caixas`}
    </span>
  );
}

export function PackagesPage() {
  const { allows } = useSession();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PackageStatus | ''>('');
  // Sem debounce, cada tecla vira uma consulta com LIKE na tabela mais quente
  // do banco.
  const debouncedSearch = useDebouncedValue(search, 300);

  // Cancelamento em dois passos, um por linha: qual encomenda esta com o
  // painel aberto e o motivo digitado nele.
  const [canceling, setCanceling] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [cancelError, setCancelError] = useState<string | null>(null);

  /** Qual entrega esta com o comprovante aberto — um por vez, como o cancelamento. */
  const [proofOf, setProofOf] = useState<string | null>(null);

  const canCancel = allows('encomendas:cancelar');

  const pages = useCursorPages();
  // Filtrar volta para a primeira pagina: o cursor apontava para uma linha da
  // lista anterior e nao significa nada na lista filtrada.
  const changeSearch = resetting(pages, setSearch);
  const changeStatus = resetting<PackageStatus | ''>(pages, setStatus);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['packages', 'list', debouncedSearch, status, pages.cursor],
    queryFn: () =>
      api.get<Page<PackageSummary>>(
        `/packages?limit=20${status ? `&status=${status}` : ''}${
          debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : ''
        }${pages.cursor ? `&cursor=${encodeURIComponent(pages.cursor)}` : ''}`,
      ),
    // Mantem a lista anterior na tela enquanto a nova busca chega, em vez de
    // piscar um estado de carregamento a cada letra.
    placeholderData: keepPreviousData,
  });

  const cancel = useMutation({
    mutationFn: (id: string) =>
      api.post<PackageDetail>(`/packages/${id}/cancelar`, { reason: reason.trim() }),
    onSuccess: () => {
      closeCancel();
      // A encomenda sai da lista padrao e os contadores da visao geral mudaram.
      void queryClient.invalidateQueries({ queryKey: ['packages'] });
    },
    onError: (error) => {
      setCancelError(
        error instanceof ApiRequestError ? error.message : 'Nao consegui cancelar agora.',
      );
    },
  });

  function openCancel(id: string) {
    setCanceling(id);
    setReason('');
    setCancelError(null);
  }

  function closeCancel() {
    setCanceling(null);
    setReason('');
    setCancelError(null);
  }

  const items = data?.items ?? [];

  /** O painel de confirmacao — o mesmo no desktop e no celular da portaria. */
  function CancelPanel({ item }: { item: PackageSummary }) {
    return (
      <div className="rounded-2xl border border-warn/40 bg-warn/10 p-4 text-left">
        <p className="text-sm font-semibold">
          Cancelar o registro #{item.code} — {item.residentName ?? item.unitLabel}?
        </p>
        <p className="mt-1 text-sm text-muted">
          {/* Num lote, "cancelar o registro" derruba VARIAS caixas de uma vez, e
              o numero precisa estar na frente de quem vai confirmar: cancelar
              tres caixas achando que cancela uma e o tipo de erro que so
              aparece quando o morador desce para buscar. Para desfazer so uma,
              o caminho e o painel de detalhes. */}
          {item.boxesAwaiting > 1
            ? `As ${item.boxesAwaiting} caixas que ainda aguardam saem da lista e o QR de retirada para de funcionar. `
            : 'A encomenda sai da lista e o QR de retirada para de funcionar. '}
          Nada e apagado: quem cancelou, quando e por que ficam no historico. Se o aviso de
          chegada ainda nao tinha saido, ele e retirado da fila — se ja saiu, avise o morador.
        </p>

        <div className="mt-3 flex flex-wrap gap-2">
          {QUICK_REASONS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => setReason(preset)}
              className={
                reason === preset
                  ? 'rounded-pill border border-accent bg-accent/10 px-3 py-1.5 text-sm font-semibold text-accent'
                  : 'rounded-pill border border-line bg-surface px-3 py-1.5 text-sm font-semibold transition hover:border-accent hover:text-accent'
              }
            >
              {preset}
            </button>
          ))}
        </div>

        <TextInput
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Motivo (obrigatorio)"
          aria-label="Motivo do cancelamento"
          maxLength={200}
          className="mt-3"
        />

        {cancelError && (
          <div className="mt-3">
            <Alert>{cancelError}</Alert>
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            className="py-2"
            // O minimo de 3 caracteres e o mesmo da API: melhor o botao ficar
            // desligado do que a portaria levar um 422 depois de confirmar.
            disabled={reason.trim().length < 3}
            loading={cancel.isPending}
            onClick={() => cancel.mutate(item.id)}
          >
            Confirmar cancelamento
          </Button>
          <Button variant="ghost" className="py-2" onClick={closeCancel}>
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Encomendas" />

      <Card>
        <CardHeader
          title="Todas as encomendas"
          subtitle="Consulta e historico completo"
          action={
            <Link to="/registrar">
              <Button>+ Nova encomenda</Button>
            </Link>
          }
        />

        <div className="space-y-3 px-5 pb-5 sm:px-6">
          <SearchInput
            value={search}
            onChange={(event) => changeSearch(event.target.value)}
            placeholder="Buscar por nome, apto, codigo ou status..."
            aria-label="Buscar encomendas"
          />

          <div className="flex flex-wrap gap-2">
            {FILTERS.map((filter) => (
              <button
                key={filter.value || 'todas'}
                type="button"
                onClick={() => {
                  changeStatus(filter.value);
                  closeCancel();
                }}
                className={
                  status === filter.value
                    ? 'rounded-pill border border-accent bg-accent/10 px-3 py-1.5 text-sm font-semibold text-accent'
                    : 'rounded-pill border border-line bg-surface-2 px-3 py-1.5 text-sm font-semibold text-muted transition hover:text-fg'
                }
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {isLoading && <LoadingRows rows={4} />}

        {!isLoading && items.length === 0 && (
          <EmptyState
            title="Nada encontrado"
            description={
              debouncedSearch
                ? 'Nenhuma encomenda corresponde a esta busca.'
                : status === 'canceled'
                  ? 'Nenhum registro foi cancelado por aqui.'
                  : 'Registre a primeira entrada para comecar.'
            }
          />
        )}

        {items.length > 0 && (
          <>
            {/* Desktop: tabela.
                O teto de altura existe para o rodape de paginacao continuar
                alcancavel: sem ele, 20 linhas empurram os botoes de pagina
                para fora da tela e a lista parece nao ter continuacao. O
                cabecalho gruda no topo da area rolavel — tabela rolada sem
                cabecalho vira uma grade de numeros sem rotulo. */}
            <div className="hidden max-h-[60vh] overflow-auto border-t border-line-soft lg:block">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 z-10 bg-surface text-[0.7rem] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="px-6 py-3 font-semibold">Codigo</th>
                    <th className="px-6 py-3 font-semibold">Morador</th>
                    <th className="px-6 py-3 font-semibold">Unidade</th>
                    <th className="px-6 py-3 font-semibold">Recebida</th>
                    <th className="px-6 py-3 font-semibold">Local</th>
                    <th className="px-6 py-3 font-semibold">Status</th>
                    {/* Sempre presente: mesmo quem nao pode cancelar abre o
                        comprovante de uma entrega ja feita. */}
                    <th className="px-6 py-3 font-semibold" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-line-soft">
                  {items.map((item) => (
                    <Fragment key={item.id}>
                      <tr className="hover:bg-surface-2/50">
                        <td className="px-6 py-4 text-muted">
                          <span className="flex flex-wrap items-center gap-2">
                            #{item.code}
                            <BoxCountBadge item={item} />
                          </span>
                        </td>
                        <td className="px-6 py-4 font-bold">{item.residentName ?? '—'}</td>
                        <td className="px-6 py-4">{item.unitLabel}</td>
                        <td className="px-6 py-4 text-muted">{formatReceived(item.receivedAt)}</td>
                        <td className="px-6 py-4 text-muted">{item.storageLocation || '—'}</td>
                        <td className="px-6 py-4">
                          <StatusBadge tone={statusTone(item.status)}>
                            {statusLabel(item.status)}
                          </StatusBadge>
                        </td>
                        <td className="px-6 py-4 text-right">
                          {/* Cancelar so encomenda aguardando. Entrega
                              confirmada nao se desfaz: aquela linha e o
                              comprovante de que a caixa saiu, com assinatura e
                              nome de quem levou — a API recusa, e o botao nem
                              aparece. Retirada troca o botao pelo caminho de
                              VER esse comprovante. */}
                          {canCancel && item.status === 'awaiting' && (
                            <Button
                              variant="quiet"
                              className="py-1.5"
                              onClick={() =>
                                canceling === item.id ? closeCancel() : openCancel(item.id)
                              }
                            >
                              {canceling === item.id ? 'Fechar' : 'Remover'}
                            </Button>
                          )}
                          {/* Sem condicao de status: foto, estado da caixa e
                              observacoes existem desde o REGISTRO, e ate aqui
                              so a entrega confirmada abria painel. Quem quisesse
                              ver a foto de uma encomenda que ainda esta na
                              portaria — o caso do morador que liga perguntando
                              se chegou amassada — nao tinha onde. */}
                          <Button
                            variant="quiet"
                            className="py-1.5"
                            onClick={() => setProofOf((open) => (open === item.id ? null : item.id))}
                          >
                            {proofOf === item.id ? 'Fechar' : 'Detalhes'}
                          </Button>
                        </td>
                      </tr>

                      {canceling === item.id && (
                        <tr>
                          <td colSpan={7} className="px-6 pb-4">
                            <CancelPanel item={item} />
                          </td>
                        </tr>
                      )}

                      {proofOf === item.id && (
                        <tr>
                          <td colSpan={7} className="px-6 pb-4">
                            <PackageDetails packageId={item.id} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: a mesma informacao em cartoes — tabela com scroll
                horizontal e inutilizavel no celular da portaria. */}
            <ul className="space-y-3 border-t border-line-soft p-4 sm:p-5 lg:hidden">
              {items.map((item) => (
                <li key={item.id} className="rounded-2xl border border-line bg-surface-2 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <span className="flex flex-wrap items-center gap-2 text-sm font-bold text-accent">
                      #{item.code}
                      <BoxCountBadge item={item} />
                    </span>
                    <StatusBadge tone={statusTone(item.status)}>
                      {statusLabel(item.status)}
                    </StatusBadge>
                  </div>
                  <p className="mt-2 text-lg font-bold">{item.residentName ?? '—'}</p>
                  <p className="text-sm text-muted">
                    Unidade {item.unitLabel} • {formatReceived(item.receivedAt)}
                  </p>
                  <p className="text-sm text-muted">Local: {item.storageLocation || '—'}</p>

                  {canCancel && item.status === 'awaiting' && (
                    <Button
                      variant="quiet"
                      className="mt-2 py-1.5"
                      onClick={() => (canceling === item.id ? closeCancel() : openCancel(item.id))}
                    >
                      {canceling === item.id ? 'Fechar' : 'Remover registro'}
                    </Button>
                  )}

                  <Button
                    variant="quiet"
                    className="mt-2 py-1.5"
                    onClick={() => setProofOf((open) => (open === item.id ? null : item.id))}
                  >
                    {proofOf === item.id ? 'Fechar detalhes' : 'Ver detalhes'}
                  </Button>

                  {canceling === item.id && (
                    <div className="mt-3">
                      <CancelPanel item={item} />
                    </div>
                  )}

                  {proofOf === item.id && (
                    <div className="mt-3">
                      <PackageDetails packageId={item.id} />
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <Pagination pages={pages} nextCursor={data?.nextCursor ?? null} loading={isFetching} />
          </>
        )}
      </Card>
    </>
  );
}
